import { withDefault } from "collection-utils";
import { isCustomDomain } from "common/customDomains";
import { forceAppID } from "./lib/force-id";

export const verifyEmailPattern = "/verifyEmail";
export const overview = "/";
export const preConnectStripe = "/preConnectStripe";
export const connectStripe = "/connectStripe";
export const preConnectMicrosoft = "/preConnectMicrosoft";
export const connectMicrosoft = "/connectMicrosoft";
export const playPattern = "/play/:id/:mode?";
export const customDomainPlayPattern = "/:mode?";

export function app(appID: string, page?: string, sub?: string): string {
    let r = `/app/${appID}/${page ?? "layout"}`;
    if (sub !== undefined) {
        r = `${r}/${sub}`;
    }
    return r;
}

export function play(appID: string): string {
    return `/play/${appID}`;
}

export function preConnectStripePath(forTemplateStore: boolean, onBehalfOf?: string): string {
    const basisURL = new URL(window.location.href);
    basisURL.pathname = preConnectStripe;

    if (onBehalfOf !== undefined) {
        basisURL.searchParams.append("onBehalfOf", onBehalfOf);
    }
    if (forTemplateStore) {
        basisURL.searchParams.append("forTemplateStore", "");
    }
    return basisURL.href;
}

export function purchaseTemplate(appID: string, onBehalfOf: string, idempotencyKey: string): string {
    return `/purchaseTemplate/${appID}?onBehalfOf=${onBehalfOf}&idempotencyKey=${encodeURIComponent(idempotencyKey)}`;
}

export function org(orgID: string | undefined): string {
    return `/o/${orgID ?? "0"}`;
}

export function newApp(orgID: string): string {
    return `/o/${orgID}/newApp`;
}

export function memberJoinedOrg(orgID: string): string {
    return `/o/${orgID}/joined`;
}

export function members(orgID: string | undefined): string {
    return `${org(orgID)}/members`;
}

export function billing(orgID: string | undefined): string {
    return `${org(orgID)}/billing`;
}

export function learn(orgID: string | undefined): string {
    return `${org(orgID ?? "0")}/learn`;
}

export function upgrade(orgID: string, includeFree?: boolean): string {
    return `${org(orgID)}/upgrade${includeFree === true ? "/new" : ""}`;
}

export function appTabs(appID: string, tab?: string): string {
    if (tab === undefined) {
        return `${app(appID, "tabs")}/tabs`;
    } else {
        return `${app(appID, "tabs")}/${tab}`;
    }
}

export function appData(appID: string): string {
    return `${app(appID, "data")}`;
}

export function appSettings(appID: string, tab?: string): string {
    if (tab === undefined) {
        return `${app(appID, "settings")}`;
    } else {
        return `${app(appID, "settings")}/${tab}`;
    }
}

export function appAdmin(appID: string): string {
    return `${app(appID, "admin")}`;
}

export interface ActionsRouteProps {
    actionID: string;
    nodeKey?: string;
}

export function appActions(appID: string, actionID: string): string {
    return `${app(appID, "actions")}/${actionID}`;
}

export function appAutoPublish(appID: string): string {
    return app(appID, "autoPublish");
}

export function fullScreen(appID: string): string {
    if (isCustomDomain(window.location.hostname)) {
        return "/full";
    }
    return `${play(appID)}/full`;
}

export function isPlayer(): boolean {
    if (forceAppID !== undefined) return true;
    const isPlayRoute = window.location.href.includes("/play/");
    return isPlayRoute || isCustomDomain(window.location.hostname);
}

export function isStorybook(): boolean {
    return window.location.host.endsWith(":9009");
}

export function isPlayerFullScreen(): boolean {
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split("/");
    const isFullPath = pathParts.length > 1 && pathParts[pathParts.length - 1] === "full";
    return isPlayer() && isFullPath;
}

export function appIdOrCustomDomain(): string {
    const match = window.location.pathname.match(/\/play\/(\w+)/);
    return match === null ? window.location.hostname : decodeURIComponent(match[1]);
}

export const appPattern = "/app/:id/:page?/:tab?";

export interface AppPatternRouteProps {
    id: string;
    page: string | undefined;
    tab: string | undefined;
}

export interface OrgPatternRouteProps {
    orgID: string;
    page: string | undefined;
}

export const appLayoutPattern = "/app/:id/layout/:tab?";
export const appDataPattern = "/app/:id/data";
export const appPreviewPattern = "/app/:id/live-preview";
export const appSettingsPattern = "/app/:id/settings/:tab?";
export const appSettingsSigninPattern = "/app/:id/settings/signin";
export const appSettingsUserAgreementsPattern = "/app/:id/settings/agreements";
export const appAdminPattern = "/app/:id/admin";
export const appActionsPattern = "/app/:id/actions/:actionID";
export const acceptAppPattern = "/acceptApp/:inviteID/:appTitle";

export const newRoute = "/new";
export const orgPattern = "/o/:orgID/:page?";
export const orgNewApp = `${orgPattern}/newApp`;
export const orgCreatePattern = `${orgPattern}/create`;
export const orgCreatedPattern = `${orgPattern}/created`;
export const orgMembersPattern = `${orgPattern}/members`;
export const orgBillingPattern = `${orgPattern}/billing`;
export const usagePattern = `${orgPattern}/usage`;
export const orgReferralPattern = `${orgPattern}/referrals`;
export const learnPattern = `${orgPattern}/learn`;
export const orgAcceptPattern = `/join/:joinID/:orgName?`;
export const dataRightsPattern = `${orgPattern}/account/rights`;

export const downloadExportPattern = "/downloadExport/:exportID";

export const appPaths: string[] = [
    appLayoutPattern,
    appDataPattern,
    appSettingsPattern,
    appAdminPattern,
    appActionsPattern,
    appPattern,
];

export const orgsPaths: string[] = [
    overview,
    orgPattern,
    newRoute,
    orgNewApp,
    orgMembersPattern,
    orgBillingPattern,
    orgReferralPattern,
    usagePattern,
    dataRightsPattern,
    verifyEmailPattern,
    orgAcceptPattern,
];

export const purchaseTemplatePattern = "/purchaseTemplate/:templateID";
export const supportCodePattern = "/support/:code";

function isAppBuilder(): boolean {
    if (forceAppID !== undefined) return false;
    return window.location.href.includes("/app/");
}

export function getPlayLinkForCurrentLocation(preferredDefaultLocation: string | undefined): string | undefined {
    if (isPlayer()) return window.location.href;
    if (!isAppBuilder()) return undefined;
    if (/\/app\/sample/.test(window.location.href)) return undefined;
    if (/\/app\/.*-template$/.test(window.location.href)) return undefined;

    return withDefault(preferredDefaultLocation, window.location.href.replace("/app/", "/play/"));
}
